import { EmptyListContent } from '@euler/app/components/list/EmptyListContent';
import { useHandleServiceSubjectPress } from '@euler/app/flows/order/pages/ServiceSubjectsScreen/functions/useHandleServiceSubjectPress';
import { useServiceSubjectsHost } from '@euler/app/flows/order/pages/ServiceSubjectsScreen/host';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ServiceSubjectType } from '@euler/model/enum';
import { countExactMatches } from '@euler/utils/countExactMatches';
import { useBehaviorSubject, useCreation } from '@euler/utils/hooks';
import Fuse, { FuseResult } from 'fuse.js';
import { MotiView } from 'moti';
import { uniqBy } from 'ramda';
import { memo, useCallback, useMemo } from 'react';
import { FlatList, ListRenderItem, StyleSheet } from 'react-native';
import { findServiceSubjectIndex } from '../functions/findServiceSubjectIndex';
import { useSelectedServiceSubjectsBottomSheetFooterHeight } from '../functions/useSelectedServiceSubjectsBottomSheetFooterHeight';
import {
  ServiceSubjectCategory,
  ServiceSubjectGroup,
  ServiceSubjectItem,
} from '../types';
import { ServiceSubjectCell } from './ServiceSubjectCell';
import { useServiceSubjectsContext } from './ServiceSubjectsContext';

type Item = {
  item: ServiceSubjectItem;
  group: ServiceSubjectGroup;
  category: ServiceSubjectCategory;
  exactMatches: number;
  customScore: number;
};

const keyExtractor = ({ item }: FuseResult<Item>) =>
  [item.category.name, item.group.title, item.item.title].join(':');

export const ServiceSubjectsSearchContent = memo(
  ({ keyword }: { keyword: string }) => {
    const { rpx } = useResponsiveContext();
    const { orderDetail$, capabilities } = useServiceSubjectsHost();
    const { categories, categoryMapById, groupMapById } =
      useServiceSubjectsContext();

    const [orderDetail] = useBehaviorSubject(orderDetail$);
    const bottomSheetFooterHeight =
      useSelectedServiceSubjectsBottomSheetFooterHeight();

    const fuse = useCreation(() => {
      return new Fuse(
        categories.flatMap(x => x.groups.flatMap(g => g.items)),
        {
          keys: ['title', 'pyInitials'],
          includeMatches: true,
          includeScore: true,
          threshold: 0.6,
          ignoreLocation: true,
        },
      );
    }, [categories]);

    const matchedResults = useMemo(() => {
      const results = fuse.search(keyword).map<FuseResult<Item>>(result => {
        const normalizedFuseScore = result.score ? 1 - result.score : 0;
        const titleMatch = result.matches?.find(x => x.key === 'title');
        const pyMatch = result.matches?.find(x => x.key === 'pyInitials');
        const group = groupMapById.get(result.item.groupId)!;
        const category = categoryMapById.get(group.categoryId!)!;

        const makeItem = (exactMatches: number): Item => ({
          item: result.item,
          group,
          category,
          exactMatches,
          customScore: exactMatches * 0.6 + normalizedFuseScore * 0.4,
        });

        if (titleMatch == null) {
          if (pyMatch == null) {
            return {
              ...result,
              item: makeItem(0),
            };
          }
          const searchValue = result.item.pyInitials ?? '';
          const exactMatches = countExactMatches(searchValue, keyword);
          return {
            ...result,
            item: makeItem(exactMatches),
          };
        } else {
          const searchValue = result.item.title;
          const exactMatches = countExactMatches(searchValue, keyword);
          return {
            ...result,
            item: makeItem(exactMatches),
          };
        }
      });
      const list = results.sort((a, b) => {
        if (Math.abs(a.item.customScore - b.item.customScore) < 0.001) {
          return b.item.exactMatches - a.item.exactMatches;
        }
        return b.item.customScore - a.item.customScore;
      });

      return uniqBy(keyExtractor, list);
    }, [categoryMapById, fuse, groupMapById, keyword]);

    const isSubjectReadonly = useCallback(
      (item: ServiceSubjectItem) => {
        return Boolean(
          (item.type === ServiceSubjectType.Inspection &&
            capabilities?.readonlyInspectionSubjects.has(item.title)) ||
            (item.type === ServiceSubjectType.Construction &&
              capabilities?.readonlyConstructionSubjects.has(item.title)),
        );
      },
      [
        capabilities?.readonlyConstructionSubjects,
        capabilities?.readonlyInspectionSubjects,
      ],
    );

    const onItemPress = useHandleServiceSubjectPress({
      isSubjectReadonly,
      memberPickerTitle: '选择技师派工',
    });

    const renderItem: ListRenderItem<FuseResult<Item>> = useCallback(
      ({
        item: {
          matches,
          item: { item },
        },
        index,
      }) => {
        return (
          <ServiceSubjectCell
            item={item}
            matches={matches}
            selected={findServiceSubjectIndex(orderDetail, item) >= 0}
            onPress={onItemPress}
            first={index === 0}
            last={index === matchedResults.length - 1}
            readonly={isSubjectReadonly(item)}
            border={index < matchedResults.length - 1}
          />
        );
      },
      [isSubjectReadonly, matchedResults.length, onItemPress, orderDetail],
    );

    return (
      <MotiView
        from={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={StyleSheet.absoluteFill}
        css={`
          background-color: #fff;
        `}
      >
        <FlatList
          data={matchedResults}
          keyExtractor={keyExtractor}
          renderItem={renderItem}
          ListEmptyComponent={
            <EmptyListContent
              emptyText={`未找到与"${keyword}"相关的服务项目\n请调整关键词并重试\n支持拼音汉字和首字母搜索`}
              messageStyle={{
                color: '#888',
                textAlign: 'center',
                lineHeight: rpx(28),
              }}
              iconColor={'#999'}
              css={`
                margin: ${15}px;
              `}
            />
          }
          contentContainerStyle={{
            paddingTop: rpx(15),
            paddingBottom: bottomSheetFooterHeight + rpx(15),
            flexGrow: 1,
          }}
        />
      </MotiView>
    );
  },
);
